import React, { Component } from "react";
import PropTypes from "prop-types";

import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import OutsideClickHandler from "react-outside-click-handler";
import { withTranslation } from "react-i18next";

import { isLoggedIn } from "../../../helpers/auth";
import { ADD_WINE_TO_WINE_LIST, REMOVE_WINE_FROM_WINE_LIST } from "../../../graphql/mutations";
import { GET_ALL_WINE_LISTS_CONTROLLED_BY_MEMBER, GET_WINE } from "../../../graphql/queries";
import urlPatterns from "../../../urls";

import Title from "../../atoms/Title";
import ButtonLink from "../../atoms/ButtonLink";
import OffCanvasTray from "../OffCanvasTray";
import OffCanvasTrayHolder from "../../molecules/OffCanvasTrayHolder";
import OffCanvasTrayHeader from "../../molecules/OffCanvasTrayHeader";
import OffCanvasTrayClose from "../../atoms/OffCanvasTrayClose";
import OffCanvasTrayHeaderTitle from "../../atoms/OffCanvasTrayHeaderTitle";
import OffCanvasTrayBody from "../../molecules/OffCanvasTrayBody";
import BottleInfoTrayAddNewHolder from "../../molecules/BottleInfoTrayAddNewHolder";

import { showErrorAlert, showInfoAlertWithComponent } from "../../../helpers/alerts";
import { logError } from "../../../helpers/logging";
import { WINE_LIST_TYPE_IDS, I18N_NAMESPACES } from "../../../helpers/constants";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import CustomAlert from "../../atoms/CustomAlert";
import CustomAlertWithActionButton from "../../atoms/CustomAlertWithActionButton";

import SavedListItemCard from "../../atoms/SavedListItemCard";
import {
  BottleInfoTrayCardButton,
  NotLoggedInMessage,
  NotLoggedInParagraph,
  NotLoggedInButtonHolder,
  BottleInfoTrayCardImageHolder,
  CreateListLabel,
  StyledButtonLink,
} from "./style";

class WineListsSidePanel extends Component {
  /* eslint no-underscore-dangle: ["error", { "allow": ["_isMounted"] }] */
  _isMounted = false;

  static propTypes = {
    memberId: PropTypes.number,
    wine: PropTypes.shape({}),
    handleOffCanvasChange: PropTypes.func.isRequired,
    allWineListsQuery: PropTypes.shape({}).isRequired,
    addWineToWineList: PropTypes.func.isRequired,
    isInBoxAndCartTray: PropTypes.bool,
    history: PropTypes.shape({}).isRequired,
    callbackAfterBookmarkingWine: PropTypes.func,
    removeWineFromWineList: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    memberId: null,
    isInBoxAndCartTray: false,
    callbackAfterBookmarkingWine: () => {},
    wine: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      isBookmarkingWine: false,
      isBottleInfoTrayOpened: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  /**
   * Shows a custom alert after a wine is removed from a saved (bookmarked) list.
   *
   * @param {String} wineListName
   * */
  showAlertAfterRemovingWineFromSavedList = (wineListName = "") => {
    const { history, t } = this.props;
    const alertComponent = (
      <CustomAlert
        message={t("myCellar:removed")}
        clickableMsg={` ${wineListName || t("myCellar:list")}${t("myCellar:view")}`}
        isClickableUppercase
        onClick={() => history && history.push(urlPatterns.MY_CELLAR_BOOKMARKED)}
      />
    );
    showInfoAlertWithComponent(alertComponent, { autoClose: 10000 });
  };

  /**
   * Handles the info tray that allows creating new list.
   * If list is successfully created, then, we automatically add the selected wine to the new list.
   *
   * @param {Object} newListData
   * */
  handleBottleInfoTrayChange = (newListData = null) => {
    const { t } = this.props;
    if (newListData) {
      const { data } = newListData || {};
      const { updateOrCreateWineList } = data || {};
      const { errors, wineList } = updateOrCreateWineList || {};
      if (errors) {
        const errorMessage = errors && errors[0].messages[0];
        logError(errorMessage);
        showErrorAlert(errorMessage || t("common:sorryTryAgain"), {
          autoClose: 10000,
        });
      } else {
        const { id } = wineList || {};
        this.handleBookmarkWines(id);
      }
    }
    this._isMounted &&
      this.setState((prevState) => ({
        isBottleInfoTrayOpened: !prevState.isBottleInfoTrayOpened,
      }));
  };

  /**
   * Executes mutation to add wine to wine list/bookmark.
   *
   * @param {number} wineListId - wine list to add wine to
   */
  handleBookmarkWines = (wineListId) => {
    const { addWineToWineList, memberId, wine, t } = this.props;

    const { product, id: wineId } = wine;
    const { slug } = product || {};

    this.setState({ isBookmarkingWine: true });
    addWineToWineList({
      variables: {
        input: {
          memberId,
          wineListId,
          wineId,
        },
      },
      refetchQueries: [
        {
          query: GET_ALL_WINE_LISTS_CONTROLLED_BY_MEMBER,
          variables: { memberId },
        },
        {
          query: GET_WINE,
          variables: { memberId, slug },
        },
      ],
      awaitRefetchQueries: true,
    })
      .then(({ data }) => {
        this.handleBookmarkResponse(data, wineId, slug);
        this.setState({ isBookmarkingWine: false });
      })
      .catch((error) => {
        logError(error);
        showErrorAlert(t("common:sorryTryAgain"), { autoClose: 10000 });
        this.setState({ isBookmarkingWine: false });
      });
  };

  /**
   * Handles the case when users click to undo the bookmarked wine in the notification alert.
   *
   * @param {Number} wineId
   * @param {Number} wineListId
   * @param {String} wineSlug
   * */
  handleRemoveWineFromWineList = (wineId, wineListId, wineSlug) => {
    const { callbackAfterBookmarkingWine, removeWineFromWineList, memberId, t } = this.props;

    this._isMounted && this.setState({ isBookmarkingWine: true });
    removeWineFromWineList({
      variables: {
        input: {
          wineListId,
          wineId,
          memberId,
        },
      },
      refetchQueries: () => [
        {
          query: GET_ALL_WINE_LISTS_CONTROLLED_BY_MEMBER,
          variables: { memberId },
        },
        {
          query: GET_WINE,
          variables: { memberId, slug: wineSlug },
        },
      ],
      awaitRefetchQueries: true,
    })
      .then(({ data }) => {
        const { removeWineFromWineList: removeWineFromWineListData } = data || {};
        const { errors, wineList } = removeWineFromWineListData || {};
        const { name: wineListName } = wineList || {};
        if (errors) {
          const [error] = errors || [];
          const { messages } = error || {};
          const [message] = messages || [];
          const errorMsg = message || t("common:sorry");
          showErrorAlert(errorMsg, { autoClose: 10000 });
        } else {
          callbackAfterBookmarkingWine();
          this.showAlertAfterRemovingWineFromSavedList(wineListName);
        }
        this._isMounted && this.setState({ isBookmarkingWine: false });
      })
      .catch((error) => {
        logError(error);
        showErrorAlert(t("common:sorry"));
        this._isMounted && this.setState({ isBookmarkingWine: false });
      });
  };

  /**
   * Handles the response object after mutation to show notifications.
   * Note: wine.product.slug is used as a param instead of this.props.wine.product.slug,
   * ...because this.props.wine is null when customer click on UNDO button in alert
   * ...and un-mounts <WineListsSidePanel> component.
   *
   * @param {Object} data
   * @param {Number} wineId
   * @param {String} slug
   * */
  handleBookmarkResponse = (data = {}, wineId, slug) => {
    const { history, callbackAfterBookmarkingWine, t } = this.props;
    const { addWineToWineList } = data || {};
    const { errors, wineList } = addWineToWineList || {};
    if (errors) {
      showErrorAlert(t("common:sorryTryAgain"), { autoClose: 10000 });
    } else {
      const { name, id: wineListId } = wineList || {};
      const alertComponent = (
        <CustomAlertWithActionButton
          message={t("myCellar:saved")}
          messageWithURL={` ${name || t("myCellar:list")}${t("myCellar:view")}`}
          onMessageWithURLClick={() => history && history.push(urlPatterns.MY_CELLAR_BOOKMARKED)}
          actionButtonCopy={t("common:undo")}
          onActionButtonClick={() => this.handleRemoveWineFromWineList(wineId, wineListId, slug)}
        />
      );
      showInfoAlertWithComponent(alertComponent, { autoClose: 10000 });
      callbackAfterBookmarkingWine();
    }
  };

  /**
   * Closes the tray when user clicks outside the wine list side panel
   * (side tray) — and crucially, when Android hardware back fires
   * inside Capacitor (OffCanvasTray's useNativeBackHandler invokes
   * this same callback). Previously gated on `!isSmallDevice()` to
   * stop click-on-toast-alert from dismissing the tray on mobile,
   * but that gate also no-op'd the hardware-back path, making the
   * back gesture exit the app instead of closing the panel. The
   * outside-click case is moot on mobile anyway: the tray fills the
   * viewport, so OutsideClickHandler never fires.
   * */
  handleOutsideClick = () => {
    const { handleOffCanvasChange } = this.props;
    handleOffCanvasChange();
  };

  /**
   * Returns a sorted list of saved (bookmarked) lists.
   *
   * @param {Array} allWineLists
   * @returns {Array}
   * */
  getSortedAllWineLists = (allWineLists = []) => {
    const defaultLists = [];
    const customLists = [];
    allWineLists &&
      allWineLists.forEach((wineList) => {
        const { wineListType } = wineList || {};
        const { id } = wineListType || {};
        const isCustomList = id === WINE_LIST_TYPE_IDS.DB_ID_WINE_LIST_TYPE_MEMBER_CUSTOM;
        const isMyFavourites = id === WINE_LIST_TYPE_IDS.DB_ID_WINE_LIST_TYPE_MEMBER_FAVOURITES;
        const isMyWishList = id === WINE_LIST_TYPE_IDS.DB_ID_WINE_LIST_TYPE_MEMBER_WISHLIST;
        if (isCustomList) {
          customLists.push(wineList);
        } else if (isMyFavourites || isMyWishList) {
          defaultLists.push(wineList);
        }
      });

    // sorts the custom lists lexicographically.
    const sortedCustomLists = customLists.sort((listA, listB) => listA.name - listB.name);

    return [...defaultLists, ...sortedCustomLists];
  };

  /**
   * Renders the content inside the side tray for non-logged in user.
   *
   * @returns {Component}
   * */
  renderContentForNonLoggedInUser = () => {
    const { handleOffCanvasChange, t } = this.props;

    return (
      <>
        <OffCanvasTrayHeader>
          <OffCanvasTrayClose onClick={handleOffCanvasChange} showBackIcon />
        </OffCanvasTrayHeader>
        <NotLoggedInMessage>
          <NotLoggedInParagraph>{t("myCellar:createAccount")}</NotLoggedInParagraph>
          <NotLoggedInButtonHolder>
            <ButtonLink to={urlPatterns.QUIZ} full>
              {t("common:takeQuiz")}
            </ButtonLink>
          </NotLoggedInButtonHolder>
        </NotLoggedInMessage>
      </>
    );
  };

  /**
   * Renders the content inside the side tray for logged in user.
   *
   * @returns {Component}
   * */
  renderContentForLoggedInUser = () => {
    const { isBookmarkingWine } = this.state;
    const { wine, handleOffCanvasChange, allWineListsQuery, t } = this.props;

    const { allWineLists, loading } = allWineListsQuery || {};
    const isLoading = allWineListsQuery.loading || isBookmarkingWine;

    const memberHasSavedLists = !loading && !isBookmarkingWine && allWineLists;

    const sortedAllWineLists = this.getSortedAllWineLists(allWineLists || []);

    return (
      <>
        <OffCanvasTrayHeader>
          <OffCanvasTrayClose onClick={handleOffCanvasChange} showBackIcon />
          <OffCanvasTrayHeaderTitle>{t("myCellar:saveToLists")}</OffCanvasTrayHeaderTitle>
        </OffCanvasTrayHeader>

        <OffCanvasTrayBody>
          <Title tag={2} uppercase hiddenSm>
            {t("myCellar:saveToLists")}
          </Title>
          {isLoading ? (
            <StyledLoading />
          ) : (
            <>
              <BottleInfoTrayCardButton onClick={() => this.handleBottleInfoTrayChange(null)}>
                <BottleInfoTrayCardImageHolder />
                <CreateListLabel>{t("myCellar:createList")}</CreateListLabel>
              </BottleInfoTrayCardButton>
              {memberHasSavedLists &&
                sortedAllWineLists.map((wineList) => {
                  const { id: wineListId } = wineList || {};
                  return (
                    <SavedListItemCard
                      wineList={wineList}
                      wine={wine}
                      handleBookmarkWines={this.handleBookmarkWines}
                      handleRemoveWineFromWineList={this.handleRemoveWineFromWineList}
                      key={wineListId}
                    />
                  );
                })}
              <StyledButtonLink to={urlPatterns.MY_CELLAR_BOOKMARKED} full fullMobile>
                {t("myCellar:viewLists")}
              </StyledButtonLink>
            </>
          )}
        </OffCanvasTrayBody>
      </>
    );
  };

  render() {
    const { isBottleInfoTrayOpened } = this.state;
    const { memberId, isInBoxAndCartTray } = this.props;

    return (
      <>
        <OffCanvasTray
          setOverflowHidden={false}
          outSideClickHandler={() => this.handleOutsideClick()}
          isOffcanvasOpened
          isInBoxAndCartTray={isInBoxAndCartTray}
        >
          <OffCanvasTrayHolder isOffcanvasOpened isInBoxAndCartTray={isInBoxAndCartTray}>
            <OutsideClickHandler
              onOutsideClick={() => {
                this._isMounted &&
                  this.setState({
                    isBottleInfoTrayOpened: false,
                  });
              }}
            >
              {isLoggedIn() && (
                <BottleInfoTrayAddNewHolder
                  handleBottleInfoTrayChange={(newListData) => this.handleBottleInfoTrayChange(newListData)}
                  isBottleInfoTrayOpened={isBottleInfoTrayOpened}
                  memberId={memberId}
                />
              )}
            </OutsideClickHandler>

            {!isLoggedIn() ? this.renderContentForNonLoggedInUser() : this.renderContentForLoggedInUser()}
          </OffCanvasTrayHolder>
        </OffCanvasTray>
      </>
    );
  }
}

export default compose(
  graphql(GET_ALL_WINE_LISTS_CONTROLLED_BY_MEMBER, {
    name: "allWineListsQuery",
    options: (props) => ({
      partialRefetch: true,
      variables: {
        memberId: props.memberId,
      },
    }),
  }),
  graphql(ADD_WINE_TO_WINE_LIST, {
    name: "addWineToWineList",
  }),
  graphql(REMOVE_WINE_FROM_WINE_LIST, {
    name: "removeWineFromWineList",
  }),
  withTranslation([I18N_NAMESPACES.MY_CELLAR, I18N_NAMESPACES.COMMON])
)(WineListsSidePanel);
